import clsx from 'clsx';
import styles from './index.module.scss';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CompanyContext } from 'index';
import StepStart from '../../../features/Driver/DriverCheckList/StepStart';
import { useNavigate, useParams } from 'react-router-dom';
import WizardSteps from 'components/WizardSteps/wizard-steps';
import YesNoQuestion from 'features/Driver/DriverCheckList/YesNoQuestion';
import { paths } from 'App';
import StepReadyToSubmit from 'features/Driver/DriverCheckList/StepReadyToSubmit';
import StepEnd from 'features/Driver/DriverCheckList/StepEnd';
import candidateService from 'store/candidate/service';
import requestService from 'store/request/service';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';

const TOTAL_STEPS = 11;

const checklistQuestionsFields = [
  {
    mainField: 'drugOrAlcohol',
    secondaryField: 'drugOrAlcoholExplanation',
  },
  {
    mainField: 'amphetamines',
    secondaryField: 'amphetaminesExplanation',
  },
  {
    mainField: 'opiates',
    secondaryField: 'opiatesExplanation',
  },
  {
    mainField: 'marijuana',
    secondaryField: 'marijuanaExplanation',
  },
  {
    mainField: 'cocaine',
    secondaryField: 'cocaineExplanation',
  },
  {
    mainField: 'pcp',
    secondaryField: 'pcpExplanation',
  },
  {
    mainField: 'otherDrugs',
    secondaryField: 'otherDrugsExplanation',
  },
  {
    mainField: 'hearing',
    secondaryField: 'hearingIsCurrent',
  },
  {
    mainField: 'skillPerformanceEvaluation',
    secondaryField: 'skillPerformanceEvaluationIsCurrent',
  },
  {
    mainField: 'alternativeVisionStandard',
    secondaryField: 'alternativeVisionStandardIsCurrent',
  },
];

function DriverCheckListPage() {
  const company = useContext(CompanyContext);
  const navigate = useNavigate();
  const params = useParams();
  const [answers, setAnswers] = useState<any>({});
  const currentStep = isNaN(Number(params.step))
    ? undefined
    : Number(params.step);

  const currentDriverQuery =
    candidateService.endpoints.getCurrentUserCandidateInfo.useQuery();
  const [saveDocumentDetails] =
    requestService.endpoints.saveDocumentDetails.useMutation();

  useEffect(() => {
    navigate(`${paths.DriverCheckListPagePath}/${params.candidateDocumentId}`);
  }, []);

  const onStart = () => {
    navigate(
      `${paths.DriverCheckListPagePath}/${params.candidateDocumentId}/1`
    );
  };

  const onNextQuestion = async (answer: boolean, explanation?: any) => {
    const newAnswers = { ...answers };
    newAnswers[`${currentStep}`] = {
      answer,
      explanation,
    };
    setAnswers(newAnswers);
    await saveDocumentDetails({
      candidateDocumentId: params.candidateDocumentId || '',
      form: {
        [checklistQuestionsFields[(currentStep || 0) - 1].mainField]: answer,
        [checklistQuestionsFields[(currentStep || 0) - 1].secondaryField]:
          explanation,
      },
      confirmPost: false,
    });
    navigate(
      `${paths.DriverCheckListPagePath}/${params.candidateDocumentId}/${
        (currentStep || 0) + 1
      }`
    );
  };

  const handleSubmit = async () => {
    await saveDocumentDetails({
      candidateDocumentId: params.candidateDocumentId || '',
      form: {},
      confirmPost: true,
    });

    navigate(
      `${paths.DriverCheckListPagePath}/${params.candidateDocumentId}/12`
    );
  };

  const handleEnd = () => {
    navigate(
      `${paths.DriverDocumentsPage}?from=${DocumentTypeEnum.DRIVER_CHECKLIST}&showOneMoreThing=true`
    );
  };

  const Steps = useMemo(() => {
    return {
      [0]: <StepStart logo={company.logo} onStart={onStart} />,
      [1]: (
        <YesNoQuestion
          key={1}
          onNext={onNextQuestion}
          title={'Drug or alcohol counseling'}
          question={
            'In the past 6 months have you received or been recommended for drug or alcohol counseling?'
          }
          secondaryQuestion={{
            question:
              'Explain. Date, Substance, How Long, How Treated, etc.): ',
            type: 'explanation',
          }}
        />
      ),
      [2]: (
        <YesNoQuestion
          key={2}
          onNext={onNextQuestion}
          title={'Amphetamines & Stimulants'}
          question={
            'In the past 6 months have you used amphetamines/stimulants of any kind?'
          }
          secondaryQuestion={{
            question:
              'Explain. Date, Substance, How Long, How Treated, etc.): ',
            type: 'explanation',
          }}
        />
      ),
      [3]: (
        <YesNoQuestion
          key={3}
          onNext={onNextQuestion}
          title={'Opiates & Opioids'}
          question={
            'In the past 6 months have you used opiates/opioids (i.e. hydro, oxy, codeine, morphine, heroin)?'
          }
          secondaryQuestion={{
            question:
              'Explain. Date, Substance, How Long, How Treated, etc.): ',
            type: 'explanation',
          }}
        />
      ),
      [4]: (
        <YesNoQuestion
          key={4}
          onNext={onNextQuestion}
          title={'Marijuana & THC'}
          question={
            'In the past 6 months have you used marijuana, hashish, or any THC?'
          }
          secondaryQuestion={{
            question:
              'Explain. Date, Substance, How Long, How Treated, etc.): ',
            type: 'explanation',
          }}
        />
      ),
      [5]: (
        <YesNoQuestion
          key={5}
          onNext={onNextQuestion}
          title={'Cocaine'}
          question={'In the past 6 months have you used cocaine?'}
          secondaryQuestion={{
            question: 'Explain. Date, How Long, How Treated, etc.): ',
            type: 'explanation',
          }}
        />
      ),
      [6]: (
        <YesNoQuestion
          key={6}
          onNext={onNextQuestion}
          title={'PCP & Hallucinogens'}
          question={
            'In the past 6 months have you used PCP or another hallucinogen?'
          }
          secondaryQuestion={{
            question:
              'Explain. Date, Substance, How Long, How Treated, etc.): ',
            type: 'explanation',
          }}
        />
      ),
      [7]: (
        <YesNoQuestion
          key={7}
          onNext={onNextQuestion}
          title={'Other Drugs'}
          question={
            'In the past 6 months have you used or taken any other illegal drug?'
          }
          secondaryQuestion={{
            question:
              'Explain. Date, Substance, How Long, How Treated, etc.): ',
            type: 'explanation',
          }}
        />
      ),
      [8]: (
        <YesNoQuestion
          key={8}
          onNext={onNextQuestion}
          title={'Hearing & Seizures'}
          question={
            'In the past 6 months have you been issued a waiver or exemption by US DOT for hearing or seizures?'
          }
          secondaryQuestion={{
            question: 'Is it current?',
            type: 'yesNo',
          }}
        />
      ),
      [9]: (
        <YesNoQuestion
          key={9}
          onNext={onNextQuestion}
          title={'Skill Performance Evaluation'}
          question={
            'In the past 6 months have you obtained a Skill Performance Evaluation (SPE) certification from the US DOT?'
          }
          secondaryQuestion={{
            question: 'Is it current?',
            type: 'yesNo',
          }}
        />
      ),
      [10]: (
        <YesNoQuestion
          key={10}
          onNext={onNextQuestion}
          title={'Alternative Vision Standard'}
          question={
            'In the past 6 months have you been sent for an alternative vision standard via the MCSA-5871?'
          }
          secondaryQuestion={{
            question: 'Have you had the required road test?',
            type: 'yesNo',
          }}
        />
      ),
      [11]: (
        <StepReadyToSubmit
          onSubmit={handleSubmit}
          driver={currentDriverQuery.data}
        />
      ),
      [12]: <StepEnd onNext={handleEnd} />,
    } as any;
  }, [currentStep, currentDriverQuery.data]);

  return (
    <div
      className={clsx({
        [styles.root]: true,
        [styles.centerContent]: [0, 12].includes(currentStep || 0),
      })}
    >
      {![0, 12].includes(currentStep || 0) && (
        <WizardSteps
          className={styles.wizard}
          totalSteps={TOTAL_STEPS}
          currentStep={currentStep || 1}
        />
      )}
      {Object.keys(Steps).map((key: string) => (
        <div
          key={`step-${key}`}
          className={clsx({
            [styles.hide]: Number(key) !== (currentStep || 0),
          })}
        >
          {Steps[key]}
        </div>
      ))}
    </div>
  );
}

export default DriverCheckListPage;
